import { CreatorModuleAction, isCreatorModuleActionAllowed } from './creator-module-access.policy';

export type StudioModuleProfile = 'social-commerce' | 'studio-only' | 'backlog';

export type StudioModuleAccessState = 'available' | 'suspended' | 'hidden';

/**
 * Resolves Studio shell access for a creator module.
 * - available: module enabled — full Studio operations
 * - suspended: module off but inventory exists (social-commerce) — read + obligations only
 * - hidden: module off and no inventory — nav hidden, routes blocked
 */
export function resolveStudioModuleAccessState(input: {
  moduleProfile: StudioModuleProfile;
  effectiveEnabled: boolean;
  recordCount: number;
}): StudioModuleAccessState {
  if (input.effectiveEnabled) {
    return 'available';
  }
  if (input.moduleProfile === 'social-commerce' && input.recordCount > 0) {
    return 'suspended';
  }
  return 'hidden';
}

export function isStudioModuleNavVisible(state: StudioModuleAccessState): boolean {
  return state === 'available' || state === 'suspended';
}

export function mapStudioAccessStateToRouteState(
  state: StudioModuleAccessState,
): 'available' | 'suspended' | 'blocked' {
  if (state === 'available') return 'available';
  if (state === 'suspended') return 'suspended';
  return 'blocked';
}

/**
 * Whether a creator may perform a module action when the module is not fully enabled.
 * When `moduleEnabled` is true, all actions allowed via {@link isCreatorModuleActionAllowed}.
 */
export function isCreatorModuleActionAllowedWithInventory(input: {
  action: CreatorModuleAction;
  moduleEnabled: boolean;
  moduleProfile: StudioModuleProfile;
  recordCount: number;
}): boolean {
  if (input.moduleEnabled) {
    return isCreatorModuleActionAllowed(input.action, true);
  }

  if (
    input.action === CreatorModuleAction.EXISTING_CUSTOMER_ACCESS ||
    input.action === CreatorModuleAction.OBLIGATION_OPERATION ||
    input.action === CreatorModuleAction.PUBLIC_DISCOVERY
  ) {
    return true;
  }

  if (
    input.moduleProfile === 'social-commerce' &&
    input.recordCount > 0 &&
    input.action === CreatorModuleAction.MANAGE_READ
  ) {
    return true;
  }

  return isCreatorModuleActionAllowed(input.action, false);
}

export const ACQUISITION_PAUSED_MESSAGE =
  'New memberships and purchases are paused for this creator right now.';
